<template>
  <v-container fluid class="pa-0 view-contacts-page">
    <v-card class="mx-auto" max-width="1400" elevation="0">

      <!-- Modern Header Section -->
      <v-container class="pb-0">
        <v-row class="mb-6 align-center animate__animated animate__fadeIn">
          <v-col cols="12">
            <div class="d-flex align-center flex-wrap">
              <v-btn icon class="mr-4" @click="beforeRoute" color="primary" outlined>
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>

              <v-avatar size="64"
                :color="data_entity.is_customer && data_entity.is_supplier ? 'purple' : data_entity.is_customer ? 'success' : 'primary'"
                class="mr-4 elevation-3">
                <span class="white--text text-h5 font-weight-black">{{ getInitials(data_entity.name) }}</span>
              </v-avatar>

              <div class="flex-grow-1">
                <h1 class="text-h4 font-weight-black grey--text text--darken-4 mb-1">
                  {{ data_entity.name }}
                </h1>
                <div class="d-flex align-center flex-wrap gap-2">
                  <v-chip v-if="data_entity.is_customer" color="success lighten-5" text-color="success darken-2"
                    class="font-weight-bold" small label>
                    <v-icon left x-small>mdi-account-check</v-icon> Cliente
                  </v-chip>
                  <v-chip v-if="data_entity.is_supplier" color="primary lighten-5" text-color="primary darken-2"
                    class="font-weight-bold" small label>
                    <v-icon left x-small>mdi-truck-delivery</v-icon> Suplidor
                  </v-chip>
                  <v-chip :color="data_entity.status_id ? 'success' : 'error'" dark label small
                    class="font-weight-bold">
                    <v-icon left x-small>{{ data_entity.status_id ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
                    {{ data_entity.status_id ? 'Activo' : 'Inactivo' }}
                  </v-chip>
                  <v-chip v-if="data_entity.business_unit_description_short" color="grey lighten-4"
                    text-color="grey darken-2" small label class="font-weight-bold">
                    <v-icon left x-small>mdi-domain</v-icon> {{ data_entity.business_unit_description_short }}
                  </v-chip>
                </div>
              </div>

              <v-spacer></v-spacer>

              <div class="d-flex align-center">
                <v-btn depressed color="primary" class="rounded-lg mr-2" @click="viewArticles">
                  <v-icon left>mdi-package-variant</v-icon>
                  Catálogo de Artículos
                </v-btn>
                <v-btn outlined color="primary" class="rounded-lg" @click="editEntity">
                  <v-icon left>mdi-pencil</v-icon>
                  Editar
                </v-btn>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>

      <v-card-text class="pa-6 pt-0">
        <search-header @dataFilters="changeBusiness" :show_department="false" :show_weeks="false" :selected_week="2"
          :type="false" />

        <!-- Summary Cards -->
        <v-row class="mb-6 mt-4">
          <v-col v-for="(item, index) in summaryCards" :key="index" cols="12" sm="6" md="3">
            <v-card class="summary-card rounded-xl" :class="item.colorClass" flat>
              <v-card-text class="pa-4">
                <div class="d-flex align-center mb-2">
                  <v-icon :color="item.iconColor" class="mr-2">{{ item.icon }}</v-icon>
                  <span class="text-overline font-weight-black grey--text text--darken-1">{{ item.title }}</span>
                </div>
                <template v-if="item.type === 'array'">
                  <div v-for="(subItem, subIndex) in getHeader(item.key)" :key="subIndex" class="d-flex align-baseline">
                    <span class="text-h5 font-weight-black mr-1" :class="item.valueColor">{{
                      currencyFormat(subItem.total_amount) }}</span>
                    <span class="text-caption font-weight-bold grey--text">{{ subItem.currency_code }}</span>
                  </div>
                  <div v-if="getHeader(item.key).length === 0" class="text-h5 font-weight-black"
                    :class="item.valueColor">
                    0.00
                  </div>
                </template>
                <div v-else class="text-h5 font-weight-black" :class="item.valueColor">
                  {{ item.prefix }}{{ currencyFormat(item.value || 0) }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Main Content Grid -->
        <v-row>
          <!-- Left Column: Entity Info & Contacts -->
          <v-col cols="12" lg="8">
            <!-- Entity Information Card -->
            <v-card class="rounded-xl mb-6 border" flat>
              <v-card-title class="py-4 px-6 border-bottom">
                <v-icon left color="primary" size="28">mdi-account-details</v-icon>
                <span class="text-h6 font-weight-bold grey--text text--darken-3">Información de la Entidad</span>
              </v-card-title>
              <v-card-text class="pa-6">
                <v-row>
                  <v-col cols="12" md="4">
                    <div class="info-item">
                      <span class="text-overline grey--text font-weight-bold d-block mb-1">Tipo de Identificación</span>
                      <div class="d-flex align-center">
                        <v-icon small color="primary" class="mr-2">mdi-card-account-details</v-icon>
                        <span class="text-body-1 font-weight-bold grey--text text--darken-4">{{
                          data_entity.document_id_type_name || 'N/A' }}</span>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="4">
                    <div class="info-item">
                      <span class="text-overline grey--text font-weight-bold d-block mb-1">Número de
                        Identificación</span>
                      <div class="d-flex align-center">
                        <v-icon small color="primary" class="mr-2">mdi-identifier</v-icon>
                        <span class="text-body-1 font-weight-black primary--text">{{ data_entity.document_id || 'N/A'
                        }}</span>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="4">
                    <div class="info-item">
                      <span class="text-overline grey--text font-weight-bold d-block mb-1">Moneda Base</span>
                      <div class="d-flex align-center">
                        <v-icon small color="primary" class="mr-2">mdi-currency-usd</v-icon>
                        <span class="text-body-1 font-weight-bold grey--text text--darken-4">{{
                          data_entity.currency_name }} ({{
                            data_entity.currency_code }})</span>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Entity Branches Card -->
            <v-card v-if="data_entity.entity_branches && data_entity.entity_branches.length > 0"
              class="rounded-xl mb-6 border" flat>
              <v-card-title class="py-4 px-6 border-bottom d-flex align-center">
                <v-icon left color="primary" size="28">mdi-office-building-marker</v-icon>
                <span class="text-h6 font-weight-bold grey--text text--darken-3">Sucursales</span>
                <v-spacer></v-spacer>
                <v-chip small color="primary lighten-5" text-color="primary" class="font-weight-bold">
                  {{ data_entity.entity_branches.length }} sucursales
                </v-chip>
              </v-card-title>
              <v-card-text class="pa-4">
                <v-row>
                  <v-col v-for="(branch, index) in data_entity.entity_branches" :key="index" cols="12" sm="6" md="4">
                    <v-card class="branch-card rounded-lg pa-3 border" flat outlined>
                      <div class="d-flex align-center">
                        <v-avatar size="36" color="primary lighten-5" class="mr-3">
                          <v-icon small color="primary">mdi-store</v-icon>
                        </v-avatar>
                        <div>
                          <div class="text-body-2 font-weight-bold grey--text text--darken-4">
                            {{ branch.supplier?.name || 'Sin nombre' }}
                          </div>
                          <div class="text-caption grey--text">
                            <v-icon x-small>mdi-map-marker</v-icon>
                            {{ branch.supplier?.address || 'Sin dirección' }}
                          </div>
                        </div>
                        <v-spacer></v-spacer>
                        <v-chip x-small :color="branch.supplier?.status ? 'success' : 'grey'" dark>
                          {{ branch.supplier?.status ? 'Activo' : 'Inactivo' }}
                        </v-chip>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Contacts Card -->
            <v-card class="rounded-xl mb-6 border" flat>
              <v-card-title class="py-4 px-6 border-bottom">
                <v-icon left color="primary" size="28">mdi-contacts</v-icon>
                <span class="text-h6 font-weight-bold grey--text text--darken-3">Direcciones / Contactos</span>
              </v-card-title>
              <v-card-text class="pa-4">
                <template v-if="data_entity.entity_contact_json && data_entity.entity_contact_json.length > 0">
                  <v-data-table :headers="headersContacts" :items="data_entity.entity_contact_json" hide-default-footer
                    class="custom-table elevation-0" :items-per-page="-1">
                    <template v-slot:[`item.description_type`]="{ item }">
                      <v-chip small :color="getContactTypeColor(item.description_type)" text-color="white" label>
                        <v-icon left x-small>{{ getContactTypeIcon(item.description_type) }}</v-icon>
                        {{ item.description_type }}
                      </v-chip>
                    </template>
                    <template v-slot:[`item.description`]="{ item }">
                      <span class="text-body-2 font-weight-medium grey--text text--darken-3">{{ item.description
                        }}</span>
                    </template>
                  </v-data-table>
                </template>
                <div v-else class="text-center py-8">
                  <v-icon size="48" color="grey lighten-2">mdi-account-off-outline</v-icon>
                  <p class="text-body-2 grey--text mt-2">No hay contactos registrados</p>
                </div>
              </v-card-text>
            </v-card>

            <!-- Payment Methods Card -->
            <v-card v-if="data_entity.supplier_payment_types && data_entity.supplier_payment_types.length > 0"
              class="rounded-xl mb-6 border" flat>
              <v-card-title class="py-4 px-6 border-bottom">
                <v-icon left color="primary" size="28">mdi-credit-card-multiple</v-icon>
                <span class="text-h6 font-weight-bold grey--text text--darken-3">Métodos de Pago Aceptados</span>
              </v-card-title>
              <v-card-text class="pa-4">
                <div class="d-flex flex-wrap gap-2">
                  <v-chip v-for="payment in data_entity.supplier_payment_types" :key="payment.id"
                    :color="getPaymentTypeColor(payment.name)" text-color="white" small class="font-weight-bold">
                    <v-icon left x-small>{{ getPaymentTypeIcon(payment.name) }}</v-icon>
                    {{ payment.name }}
                  </v-chip>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Right Column: Configuration -->
          <v-col cols="12" lg="4">
            <!-- Configuration Card -->
            <v-card class="rounded-xl mb-6 border sticky-card" flat>
              <v-card-title class="py-4 px-6 border-bottom">
                <v-icon left color="primary" size="28">mdi-cog</v-icon>
                <span class="text-h6 font-weight-bold grey--text text--darken-3">Configuración</span>
              </v-card-title>
              <v-card-text class="pa-4">
                <!-- Customer Configuration -->
                <template v-if="data_entity.is_customer">
                  <div class="config-section mb-4">
                    <div class="d-flex align-center mb-3">
                      <v-avatar size="32" color="success lighten-5" class="mr-2">
                        <v-icon small color="success">mdi-account-check</v-icon>
                      </v-avatar>
                      <span class="text-subtitle-1 font-weight-bold success--text">Configuración Cliente</span>
                    </div>
                    <div class="config-item mb-2">
                      <v-icon small color="grey" class="mr-2">mdi-file-document-outline</v-icon>
                      <span class="text-caption grey--text">NCF:</span>
                      <span class="text-body-2 font-weight-bold ml-2">{{ selectedNfcCustomers.name || 'No configurado'
                        }}</span>
                    </div>
                    <div class="config-item mb-2">
                      <v-icon small color="grey" class="mr-2">mdi-calendar-clock</v-icon>
                      <span class="text-caption grey--text">Plazo de pago:</span>
                      <span class="text-body-2 font-weight-bold ml-2">{{ selectedCreditCustomers.name || 'No
                        configurado' }}</span>
                    </div>
                    <div v-if="selectedRetencionesClientes && selectedRetencionesClientes.name"
                      class="config-item mb-2">
                      <v-icon small color="grey" class="mr-2">mdi-percent</v-icon>
                      <span class="text-caption grey--text">Retención:</span>
                      <span class="text-body-2 font-weight-bold ml-2">{{ selectedRetencionesClientes.name }}</span>
                    </div>
                    <div v-if="selectedAccountClientes && selectedAccountClientes.text" class="config-item">
                      <v-icon small color="grey" class="mr-2">mdi-cash-plus</v-icon>
                      <span class="text-caption grey--text">Cuenta CxC:</span>
                      <span class="text-body-2 font-weight-bold ml-2">{{ selectedAccountClientes.text }}</span>
                    </div>
                  </div>
                  <v-divider class="my-4"></v-divider>
                </template>

                <!-- Supplier Configuration -->
                <template v-if="data_entity.is_supplier">
                  <div class="config-section">
                    <div class="d-flex align-center mb-3">
                      <v-avatar size="32" color="primary lighten-5" class="mr-2">
                        <v-icon small color="primary">mdi-truck-delivery</v-icon>
                      </v-avatar>
                      <span class="text-subtitle-1 font-weight-bold primary--text">Configuración Suplidor</span>
                    </div>
                    <div class="config-item mb-2">
                      <v-icon small color="grey" class="mr-2">mdi-file-document-outline</v-icon>
                      <span class="text-caption grey--text">NCF:</span>
                      <span class="text-body-2 font-weight-bold ml-2">{{ selectedNfcSuppliers.name || 'No configurado'
                        }}</span>
                    </div>
                    <div class="config-item mb-2">
                      <v-icon small color="grey" class="mr-2">mdi-calendar-clock</v-icon>
                      <span class="text-caption grey--text">Plazo de pago:</span>
                      <span class="text-body-2 font-weight-bold ml-2">{{ selectedCreditSuppliers.name || 'No
                        configurado' }}</span>
                    </div>
                    <div v-if="selectedRetencionesSuplidor && selectedRetencionesSuplidor.name"
                      class="config-item mb-2">
                      <v-icon small color="grey" class="mr-2">mdi-percent</v-icon>
                      <span class="text-caption grey--text">Retención:</span>
                      <span class="text-body-2 font-weight-bold ml-2">{{ selectedRetencionesSuplidor.name }}</span>
                    </div>
                    <div v-if="selectedAccountSuplidor && selectedAccountSuplidor.text" class="config-item">
                      <v-icon small color="grey" class="mr-2">mdi-cash-minus</v-icon>
                      <span class="text-caption grey--text">Cuenta CxP:</span>
                      <span class="text-body-2 font-weight-bold ml-2">{{ selectedAccountSuplidor.text }}</span>
                    </div>
                  </div>
                </template>
              </v-card-text>
            </v-card>

            <!-- Quick Stats Card -->
            <v-card class="rounded-xl border" flat>
              <v-card-title class="py-4 px-6 border-bottom">
                <v-icon left color="primary" size="28">mdi-chart-box</v-icon>
                <span class="text-h6 font-weight-bold grey--text text--darken-3">Estadísticas</span>
              </v-card-title>
              <v-card-text class="pa-4">
                <div class="stat-item d-flex justify-space-between align-center py-2 border-bottom">
                  <span class="text-body-2 grey--text">Facturas de Venta</span>
                  <span class="text-body-1 font-weight-bold primary--text">{{ invoice_sales.length }}</span>
                </div>
                <div class="stat-item d-flex justify-space-between align-center py-2 border-bottom">
                  <span class="text-body-2 grey--text">Facturas de Compra</span>
                  <span class="text-body-1 font-weight-bold primary--text">{{ invoice_shop.length }}</span>
                </div>
                <div class="stat-item d-flex justify-space-between align-center py-2">
                  <span class="text-body-2 grey--text">Sucursales</span>
                  <span class="text-body-1 font-weight-bold primary--text">{{ data_entity.entity_branches?.length || 0
                  }}</span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Transactions Table Card -->
        <v-card outlined class="rounded-xl mt-6 border" flat>
          <v-tabs v-model="tabSelected" background-color="transparent" class="modern-tabs px-4 pt-4" slider-size="3">
            <v-tab v-for="item in items" :key="item" class="text-capitalize font-weight-bold">
              {{ item }}
            </v-tab>
          </v-tabs>
          <v-divider></v-divider>
          <v-card-text class="pa-6">
            <v-text-field v-model="search" append-icon="mdi-magnify" label="Buscar en transacciones..." single-line
              hide-details outlined dense class="mb-4 rounded-lg" style="max-width: 400px;"></v-text-field>
            <v-data-table :headers="headers" :items="data" :search="search" :loading="loading" :options.sync="options"
              :server-items-length="totalItems" :footer-props="{
                'items-per-page-options': [5, 10, 15, 20],
                'show-current-page': true,
                'show-first-last-page': true
              }" class="custom-table elevation-0">
              <template v-slot:[`item.order_id`]="{ item }">
                <v-btn text small color="primary" class="font-weight-bold" @click="showOrder(item.order_id, false)">
                  {{ filLeft(item.order_id.toString(), 12, "0") }}
                </v-btn>
              </template>
              <template v-slot:[`item.invoice_number`]="{ item }">
                <span @click="showInvoices(item.invoice_id, true)"
                  class="primary--text font-weight-bold cursor-pointer hover-underline">
                  {{ item.invoice_number }}
                </span>
              </template>
              <template v-slot:[`item.date`]="{ item }">
                <span class="text-body-2">{{ getDate(item.emission_date) }}</span>
              </template>
              <template v-slot:[`item.expire_at`]="{ item }">
                <span v-if="item.expire_at" :class="getDateColor(item)" class="font-weight-medium">
                  {{ getDate(item.expire_at) }}
                </span>
              </template>
              <template v-slot:[`item.total_amount`]="{ item }">
                <span class="font-weight-bold">{{ currencyFormat(item.total_amount) }}</span>
                <span class="text-caption grey--text ml-1">{{ item.currency_code }}</span>
              </template>
              <template v-slot:[`item.total_paid_amount`]="{ item }">
                <span class="font-weight-bold success--text">{{ currencyFormat(item.total_paid_amount) }}</span>
                <span class="text-caption grey--text ml-1">{{ item.currency_code }}</span>
              </template>
              <template v-slot:[`item.cobrar`]="{ item }">
                <span class="font-weight-bold"
                  :class="(item.total_amount - item.total_paid_amount) > 0 ? 'error--text' : 'success--text'">
                  {{ currencyFormat(item.total_amount - item.total_paid_amount) }}
                </span>
                <span class="text-caption grey--text ml-1">{{ item.currency_code }}</span>
              </template>
              <template v-slot:[`item.status`]="{ item }">
                <v-chip small :color="time_line_orders(item.last_status_id).color" text-color="white" label
                  class="font-weight-bold">
                  {{ item.last_status_name }}
                </v-chip>
              </template>
              <template v-slot:[`item.status_invoice`]="{ item }">
                <v-chip small :color="time_line_orders(item.status_id).color" text-color="white" label
                  class="font-weight-bold">
                  {{ item.sta_description }}
                </v-chip>
              </template>
              <template v-slot:no-data>
                <div class="text-center py-8">
                  <v-icon x-large color="grey lighten-1" class="mb-4">mdi-database-off</v-icon>
                  <div class="text-subtitle-1 grey--text">No hay datos disponibles</div>
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-card-text>
    </v-card>
  </v-container>
</template>


<script>
import moment from "moment";

export default {
  data: () => ({
    entityStatus: 'active',
    tabSelected: 0,
    dialog: false,
    dialogDelete: false,
    headers: [],
    data: [],
    search: "",
    editedIndex: -1,
    editedItem: [],
    defaultItem: [],
    itemsBussines: [],
    items: [
      "Facturas ventas",
      "Facturas compras",
      "Transacciones",
      "Cotizaciones",
      "Conduces",
      "Ordenes de compra",
    ],
    headersContacts: [
      { text: "Tipo", align: "start", sortable: false, value: "description_type", width: "30%" },
      { text: "Descripción", value: "description" },
    ],
    id_entity: null,
    data_entity: {},
    documents: [],
    loading: false,
    ncfTypes: [],
    selectedNfcCustomers: {},
    selectedNfcSuppliers: {},
    selectedCreditCustomers: {},
    selectedCreditSuppliers: {},
    selectedRetencionesClientes: {},
    selectedRetencionesSuplidor: {},
    selectedAccountClientes: {},
    selectedAccountSuplidor: {},
    cxc: [],
    cxp: [],
    accounts: [],
    invoice_sales: [],
    invoice_shop: [],
    provincies: [],
    credit_days: [],
    withholdings: [],
    lastFilters: {},
    options: {},
    totalItems: 0,
    searchTimeout: null,
  }),

  computed: {
    summaryCards() {
      return [
        {
          title: "Cuentas por Cobrar",
          type: "array",
          key: "porCobrar",
          icon: "mdi-cash-plus",
          iconColor: "success",
          colorClass: "card-success",
          valueColor: "success--text"
        },
        {
          title: "Cuentas por Pagar",
          type: "array",
          key: "porPagar",
          icon: "mdi-cash-minus",
          iconColor: "error",
          colorClass: "card-error",
          valueColor: "error--text"
        },
        {
          title: "Notas Débito",
          value: 0,
          prefix: "",
          icon: "mdi-file-document-plus",
          iconColor: "warning",
          colorClass: "card-warning",
          valueColor: "warning--text text--darken-2"
        },
        {
          title: "Notas Crédito",
          value: 0,
          prefix: "",
          icon: "mdi-file-document-minus",
          iconColor: "info",
          colorClass: "card-info",
          valueColor: "info--text"
        },
      ];
    },
  },

  created() {
    this.initialize();
  },

  watch: {
    tabSelected() {
      this.options.page = 1; // Reset to page 1 on tab change
      this.loadMenu();
    },
    options: {
      handler() {
        this.loadMenu();
      },
      deep: true,
    },
    search(val) {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.options.page = 1;
        this.loadMenu();
      }, 500); // 500ms debounce
    },
  },

  methods: {
    getInitials(name) {
      if (!name) return "?";
      const parts = name.split(" ");
      let initials = "";
      for (let i = 0; i < Math.min(parts.length, 2); i++) {
        if (parts[i].length > 0) {
          initials += parts[i][0];
        }
      }
      return initials.toUpperCase();
    },
    getDate(item) {
      return moment(item).format("DD/MM/YYYY");
    },
    changeBusiness(filters) {
      this.lastFilters = filters;
      this.loadData();
    },
    getDateColor(item) {
      const now = moment();
      const end = moment(item.expire_at);
      if (item.total_amount - item.total_paid_amount < 1) {
        return "success--text";
      }
      if (moment(now.format("YYYY/MM/DD")).isAfter(moment(end.format("YYYY/MM/DD")))) {
        return "error--text";
      }
      return "grey--text text--darken-2";
    },
    async loadMenu() {
      if (!this.lastFilters.business) return; // Wait for filters to be ready

      this.loading = true;
      this.data = [];

      const { page, itemsPerPage } = this.options;
      const paginationPayload = {
        entity_id: this.id_entity,
        page: page,
        limit: itemsPerPage === -1 ? 100 : itemsPerPage, // Handle 'All' typically not used here but safe fallback
        search: this.search,
      };

      const menuConfigs = {
        0: { // Facturas ventas
          headers: [
            { text: "Factura", align: "start", value: "invoice_number" },
            { text: "Unidad negocio", value: "business_unit_description" },
            { text: "Location", value: "location_description" },
            { text: "Cliente", value: "entity_name" },
            { text: "Creacion", value: "emission_date" },
            { text: "Vencimiento", value: "expire_at" },
            { text: "Total", value: "total_amount", align: "end" },
            { text: "Moneda", value: "currency_code" },
            { text: "Status", value: "status_invoice" },
          ],
          filters: this.lastFilters.business ? `AND INVOICES.BUSINESS_UNIT_ID=${this.lastFilters.business}` : "",
          endpoint: "entity/all_sales_invoices",
        },
        1: { // Facturas compras
          headers: [
            { text: "Numero", align: "start", value: "invoice_number" },
            { text: "Cliente", value: "entity_name" },
            { text: "Creacion", value: "emission_date" },
            { text: "Vencimiento", value: "expire_at" },
            { text: "Total", value: "total_amount" },
            { text: "Moneda", value: "currency_code" },
            { text: "Estado", value: "status_invoice" },
          ],
          filters: this.lastFilters.business ? `AND INVOICES.BUSINESS_UNIT_ID=${this.lastFilters.business}` : "",
          endpoint: "entity/all_purchase_invoices",
        },
        2: { // Transacciones
          headers: [
            { text: "Numero", align: "start", value: "payment_number" },
            { text: "Fecha", value: "date" },
            { text: "Monto", value: "total_amount" },
            { text: "Moneda", value: "currency_code" },
            { text: "Estado", value: "status_id" },
          ],
          filters: ``, // Add business filter if transactions table has it
          endpoint: "entity/all_transactions",
        },
        3: { // Cotizaciones
          headers: [
            { text: "Numero", align: "start", value: "invoice_number" },
            { text: "Fecha", value: "emission_date" },
            { text: "Vence", value: "expire_at" },
            { text: "Monto", value: "total_amount" },
            { text: "Moneda", value: "currency_code" },
            { text: "Estado", value: "status" },
          ],
          filters: this.lastFilters.business ? `AND I.BUSINESS_UNIT_ID=${this.lastFilters.business}` : "",
          endpoint: "entity/all_quotes",
        },
        4: { // Conduces
          headers: [
            { text: "Tracking", align: "start", value: "tracking_number" },
            { text: "Factura", value: "invoice_number" },
            { text: "Fecha Despacho", value: "dispatch_date" },
            { text: "Estado", value: "delivery_status" },
          ],
          filters: ``,
          endpoint: "entity/all_conduces",
        },
        5: { // Ordenes de compra
          headers: [
            { text: "Orden", align: "start", value: "order_id" },
            { text: "Unidad negocio", value: "business_unit_description_long" },
            { text: "Location", value: "location_description_long" },
            { text: "Suplidor", value: "entity_name" },
            { text: "Fecha", value: "created_at" },
            { text: "Total", value: "total_amount" },
            { text: "Status", value: "status" },
          ],
          filters: this.lastFilters.business ? `AND O.BUSINESS_UNIT_ID=${this.lastFilters.business}` : "",
          endpoint: "entity/all_purchase_orders",
        },
      };

      const config = menuConfigs[this.tabSelected];
      if (config) {
        this.headers = config.headers;
        try {
          // Combine standard pagination payload with specific filters
          const payload = {
            ...paginationPayload,
            filters: config.filters
          };

          const response = await this.api_Post(config.endpoint, payload);
          const result = await response.json();

          this.data = result.data || [];
          this.totalItems = result.total || 0;

        } catch (error) {
          console.error("Error loading table data:", error);
          this.data = [];
          this.totalItems = 0;
        }
      }

      this.loading = false;
    },
    getHeader(type) {
      const result = [];
      const source = type === "porPagar" ? this.cxp : this.cxc;

      source?.reduce((res, value) => {
        if (!res[value.currency_code]) {
          res[value.currency_code] = { currency_code: value.currency_code, total_amount: 0 };
          result.push(res[value.currency_code]);
        }
        res[value.currency_code].total_amount += parseFloat(value.total_pending_amount);
        return res;
      }, {});

      return result;
    },
    viewArticles() {
      this.$router.push({ name: "CatalogueSupplier", params: { id: this.id_entity } });
    },
    editEntity() {
      this.$router.push({ name: "EditContacts", params: { id: this.id_entity } });
    },
    async loadData() {
      this.loading = true;

      try {
        const [entityData, accountsData, documentsData, creditDaysData, ncfTypesData, withholdingsData] = await Promise.all([
          this.api_Get(`entity/entity_id/${this.id_entity}`).then(res => res.json()),
          this.api_Get("accounting/accountsUsable").then(res => res.json()),
          this.api_Get("accounting/documents").then(res => res.json()),
          this.api_Get("accounting/credit_days").then(res => res.json()),
          this.api_Get("accounting/nfc_types").then(res => res.json()),
          this.api_Get("accounting/withholdings").then(res => res.json()),
        ]);

        this.data_entity = entityData[0] || {};
        this.accounts = accountsData.map(item => ({
          text: item.name,
          value: item.id,
          dgii_category_id: item.dgii_category_id,
        }));
        this.documents = documentsData;

        if (this.data_entity.is_customer && this.data_entity.customer_details?.length > 0) {
          const customerDetails = this.data_entity.customer_details[0].customer;
          this.selectedCreditCustomers = creditDaysData.find(element => element.id == customerDetails.credit_day_id) || {};
          this.selectedNfcCustomers = ncfTypesData.find(element => element.id == customerDetails.nfc_type_id) || {};
          this.selectedRetencionesClientes = withholdingsData.find(element => element.id == customerDetails.withholding_id) || {};
          this.selectedAccountClientes = this.accounts.find(element => element.value == customerDetails.account_payable_id) || {};
        }

        if (this.data_entity.is_supplier && this.data_entity.supplier_details?.length > 0) {
          const supplierDetails = this.data_entity.supplier_details[0].supplier;
          this.selectedAccountSuplidor = this.accounts.find(element => element.value == supplierDetails.account_receivable_id) || {};
          this.selectedRetencionesSuplidor = withholdingsData.find(element => element.id == supplierDetails.withholding_id) || {};
          this.selectedNfcSuppliers = ncfTypesData.find(element => element.id == supplierDetails.nfc_type_id) || {};
          this.selectedCreditSuppliers = creditDaysData.find(element => element.id == supplierDetails.credit_day_id) || {};
        }

        // await this.loadCredits();
        await this.loadMenu();
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        this.loading = false;
      }
    },
    async loadCredits() {
      try {
        let filters = " and entity_id=" + this.id_entity;
        let filtersShop = " and entity_id=" + this.id_entity;

        const [salesResponse, shopResponse] = await Promise.all([
          this.api_Post("income/invoice_sales_list", filters),
          this.api_Post("income/invoice_sales_list", filtersShop),
        ]);

        if (salesResponse.status === 200) {
          this.invoice_sales = await salesResponse.json();
        }
        if (shopResponse.status === 200) {
          this.invoice_shop = await shopResponse.json();
        }
      } catch (error) {
        console.error("Error loading credits:", error);
      }
    },
    initialize() {
      this.id_entity = this.$route.params.id;
    },
    beforeRoute() {
      this.$router.go(-1);
    },
    showInvoices(id) {
      let route = this.$router.resolve({
        name: "ViewInvoice",
        params: { id: id },
      });
      window.open(route.href);
    },
    showOrder(orderId) {
      let route = this.$router.resolve({
        name: "ViewOrders",
        params: { id: orderId },
      });
      window.open(route.href);
    },
    currencyFormat(value) {
      if (value === null || value === undefined) return "0.00";
      return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
    },
    filLeft(value, length, char) {
      return value.padStart(length, char);
    },
    time_line_orders(statusId) {
      const colors = {
        1: 'success',
        2: 'warning',
        3: 'error',
        4: 'grey'
      };
      return { color: colors[statusId] || 'primary' };
    },
    getContactTypeColor(type) {
      const colors = {
        'Teléfono': 'blue',
        'Email': 'teal',
        'Dirección': 'orange',
        'Celular': 'purple',
        'Fax': 'grey',
        'default': 'blue-grey'
      };
      return colors[type] || colors.default;
    },
    getContactTypeIcon(type) {
      const icons = {
        'Teléfono': 'mdi-phone',
        'Email': 'mdi-email',
        'Dirección': 'mdi-map-marker',
        'Celular': 'mdi-cellphone',
        'Fax': 'mdi-fax',
        'default': 'mdi-card-text'
      };
      return icons[type] || icons.default;
    },
    getPaymentTypeColor(type) {
      const colors = {
        'EFECTIVO': 'green',
        'TARJETA': 'purple',
        'CHEQUE': 'blue-grey',
        'TRANSFERENCIA': 'blue',
        'DEPOSITO': 'teal',
        'BONOS': 'orange',
        'CREDITO EMPLEADO': 'deep-purple',
        'default': 'grey'
      };
      return colors[type] || colors.default;
    },
    getPaymentTypeIcon(type) {
      const icons = {
        'EFECTIVO': 'mdi-cash',
        'TARJETA': 'mdi-credit-card',
        'CHEQUE': 'mdi-checkbook',
        'TRANSFERENCIA': 'mdi-bank-transfer',
        'DEPOSITO': 'mdi-bank',
        'BONOS': 'mdi-ticket-percent',
        'CREDITO EMPLEADO': 'mdi-account-cash',
        'default': 'mdi-currency-usd'
      };
      return icons[type] || icons.default;
    },
  },
};
</script>

<style scoped>
.view-contacts-page {
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  min-height: 100vh;
}

.animate__animated {
  animation-duration: 0.6s;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate__fadeIn {
  animation-name: fadeIn;
}

.gap-2 {
  gap: 8px;
}

.border {
  border: 1px solid #e2e8f0 !important;
}

.border-bottom {
  border-bottom: 1px solid #e2e8f0 !important;
}

.summary-card {
  border: 1px solid rgba(0, 0, 0, 0.05) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.summary-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px -8px rgba(0, 0, 0, 0.1) !important;
}

.summary-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  border-radius: 4px 0 0 4px;
}

.card-success {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.08) 0%, #ffffff 100%) !important;
}

.card-success::before {
  background: #4caf50;
}

.card-error {
  background: linear-gradient(135deg, rgba(244, 67, 54, 0.08) 0%, #ffffff 100%) !important;
}

.card-error::before {
  background: #f44336;
}

.card-warning {
  background: linear-gradient(135deg, rgba(255, 152, 0, 0.08) 0%, #ffffff 100%) !important;
}

.card-warning::before {
  background: #ff9800;
}

.card-info {
  background: linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, #ffffff 100%) !important;
}

.card-info::before {
  background: #2196f3;
}

.info-item {
  background: #f8fafc;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid #edf2f7;
}

.config-section {
  background: #f8fafc;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #edf2f7;
}

.config-item {
  display: flex;
  align-items: center;
  padding: 8px 0;
}

.branch-card {
  transition: all 0.2s ease;
}

.branch-card:hover {
  background: #f8fafc !important;
  border-color: var(--v-primary-base) !important;
}

.sticky-card {
  position: sticky;
  top: 80px;
}

.modern-tabs .v-tab {
  text-transform: none !important;
  letter-spacing: 0 !important;
}

.modern-tabs .v-tab--active {
  background: rgba(var(--v-primary-base), 0.05);
}

.custom-table {
  background: transparent !important;
}

.custom-table th {
  background: #f8fafc !important;
  text-transform: uppercase;
  font-size: 11px !important;
  font-weight: 700 !important;
  color: var(--v-primary-base) !important;
  letter-spacing: 0.05em;
}

.cursor-pointer {
  cursor: pointer;
}

.hover-underline:hover {
  text-decoration: underline !important;
}
</style>